import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  CreateColumnValidationSchema,
  TCreateColumnValidation,
  useCreateColumnMutation,
} from '../../api/create-column.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_COLUMN_QUERY_KEY } from '../../api/get-list-column.api';
import { handleDomBubbleEvent } from '@/utils/dom';
import { cn } from '@/utils/cn';
import { COLUMN_COLOR } from '../../constants/column.constant';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { Input } from '@/components/_ui/input';

/**
 *
 */
export type TCreateColumnModalType = 'CreateColumnModal';
export interface TCreateColumnModalProps {
  lastColumnIndex: string;
  spaceId: string;
}
const CreateColumnModal = memo(({ spaceId, lastColumnIndex }: TCreateColumnModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TCreateColumnValidation>({
    resolver: zodResolver(CreateColumnValidationSchema),
    mode: 'onChange',
  });

  const mutationCreateColumn = useCreateColumnMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error) || 'Error creating ticketing space column, please try again!';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([
          // queryClient.invalidateQueries({
          //   queryKey: [GET_SINGLE_SPACE_QUERY_KEY, { spaceId }],
          // }),
          // queryClient.invalidateQueries({
          //   queryKey: [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
          // }),
          queryClient.invalidateQueries({
            queryKey: [
              GET_LIST_COLUMN_QUERY_KEY,
              {
                spaceId: spaceId,
                search: undefined,
                page: undefined,
                limit: undefined,
                sortDir: undefined,
                sortBy: undefined,
              },
            ],
          }),
        ]);
        modalActions.closeModal('CreateColumnModal');
        form.reset();
        showToast({
          message: 'Ticketing space column created successfully',
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenCreateColumnModal = useMemo(() => {
    return modalActions.checkIsOpenModal('CreateColumnModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationCreateColumn.isPending || !form.formState.isValid;
  }, [mutationCreateColumn.isPending, form.formState.isValid]);

  const isLoadingForm = useMemo(() => {
    return mutationCreateColumn.isPending;
  }, [mutationCreateColumn.isPending]);

  // EFFECTS
  // --- Sync with props ---
  useEffect(() => {
    if (lastColumnIndex !== undefined || spaceId) {
      // const newCreateTicketingSpaceColumnDefaultValues = lastColumnIndex + 1;
      form.reset({
        columnIndex: lastColumnIndex,
        spaceId: spaceId,
        name: '',
        color: '',
      });
    }
  }, [lastColumnIndex, spaceId]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('CreateColumnModal');
  }, [modalActions]);

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  const handleSubmit = useCallback(
    (data: TCreateColumnValidation) => {
      // console.log({
      //   columnIndex: data.columnIndex,
      //   spaceId: data.spaceId,
      //   name: data.name,
      //   color: data.color,
      // });
      mutationCreateColumn.mutate({
        columnIndex: data.columnIndex,
        spaceId: data.spaceId,
        name: data.name,
        color: data.color,
      });
    },
    [mutationCreateColumn]
  );

 

  return (
    <Modal
      isOpen={isOpenCreateColumnModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[500px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Add Column</ModalTitle>
        <ModalDescription>Add a column to define a new stage in your workflow.</ModalDescription>
      </ModalHeader>
      <ModalContent>
        <form
          id="add-ticketing-space-column-form"
          className="flex flex-col gap-[16px]"
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmit)(e);
          }}
        >
          <div className="flex flex-col gap-[8px]">
            <label htmlFor="name" className="text-[12px] text-[#4B4B4B] font-[700]">
              Name
            </label>
            <Input {...form.register('name')} id="name" placeholder="Enter column name" className="bg-white" />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-[8px] px-[2px]">
            <label htmlFor="color" className="text-[12px] text-[#4B4B4B] font-[700]">
              Color
            </label>
            <div className="flex gap-[8px] pb-0.5">
              {Object.entries(COLUMN_COLOR).map(([_key, color], index) => {
                const isSelected = form.watch('color') === (color as string);

                return (
                  <button
                    key={index + 1}
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      form.setValue('color', color as string, { shouldValidate: true });
                    }}
                    className={cn('w-[16px] h-[16px] rounded-full cursor-pointer border transition-shadow', {
                      'border-[#0C8DBF] ring-2 ring-[#0C8DBF]/40': isSelected,
                      'border-transparent': !isSelected,
                    })}
                    style={{ backgroundColor: color as string }}
                  ></button>
                );
              })}
            </div>
            {form.formState.errors.color && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.color.message}</p>
            )}
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="add-ticketing-space-column-form" type="submit" disabled={isDisabledForm}>
          {isLoadingForm ? <Spinner className="w-[18px] h-[18px]" /> : null} Create
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
CreateColumnModal.displayName = 'CreateColumnModal';

export default CreateColumnModal;
